import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LINK_TYPE_LABELS, ORIGIN_LABELS, STORE_CHIPS } from "@/lib/affiliate-ui";
import type { HistoryFilters, HistoryPeriod } from "@/lib/hooks/useLinkHistory";

interface HistoryFiltersBarProps {
  readonly filters: HistoryFilters;
  readonly onChange: (next: HistoryFilters) => void;
}

const PERIOD_OPTIONS: ReadonlyArray<readonly [HistoryPeriod, string]> = [
  ["7d", "Últimos 7 dias"],
  ["30d", "Últimos 30 dias"],
  ["90d", "Últimos 90 dias"],
  ["all", "Todo o período"],
];

const SELECT_CLASS =
  "h-9 w-full rounded-lg border border-[hsl(var(--border-strong))] bg-background/60 px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const MARKETPLACE_OPTIONS = [
  ...STORE_CHIPS.map((chip) => [chip.code, chip.label.replace(/^Lojas /, "")] as const),
  ["custom", "Link avulso"] as const,
];

/** Filtros do historico: plataforma, origem, destino, tipo de link e periodo. */
export function HistoryFiltersBar({ filters, onChange }: HistoryFiltersBarProps): JSX.Element {
  const set = <K extends keyof HistoryFilters>(key: K, value: HistoryFilters[K]): void =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <FilterSelect id="history-marketplace" label="Plataforma" value={filters.marketplace} onChange={(v) => set("marketplace", v)}
        options={MARKETPLACE_OPTIONS} />
      <FilterSelect id="history-origin" label="Origem" value={filters.origin} onChange={(v) => set("origin", v)}
        options={Object.entries(ORIGIN_LABELS)} />
      <div className="space-y-1.5">
        <Label htmlFor="history-destination" variant="field">Destino</Label>
        <Input
          id="history-destination"
          value={filters.destination}
          onChange={(event) => set("destination", event.target.value)}
          placeholder="Nome do grupo ou canal"
          className="h-9"
        />
      </div>
      <FilterSelect id="history-type" label="Tipo de link" value={filters.linkType} onChange={(v) => set("linkType", v)}
        options={Object.entries(LINK_TYPE_LABELS)} />
      <div className="space-y-1.5">
        <Label htmlFor="history-period" variant="field">Período</Label>
        <select
          id="history-period"
          className={SELECT_CLASS}
          value={filters.period}
          onChange={(event) => set("period", event.target.value as HistoryPeriod)}
        >
          {PERIOD_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

interface FilterSelectProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: ReadonlyArray<readonly [string, string]>;
}

function FilterSelect({ id, label, value, onChange, options }: FilterSelectProps): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} variant="field">{label}</Label>
      <select id={id} className={SELECT_CLASS} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Todas</option>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
    </div>
  );
}
